import { Flex } from '@libs/ui/ds/lib/components/primitiveLayouts/Flex/base/Flex'
import { Text } from '@libs/ui/ds/lib/components/typography/Text/base/Text'
import { cva } from 'class-variance-authority'
import type { FC } from 'react'

import { useTimelineContext } from '../../Timeline.context'

import type { TimelineItemProps } from './TimelineItem.types'

const timelineItemCVA = {
  root: cva(['group/item w-fit gap-4']),
  header: cva(['!flex-nowrap'], {
    variants: {
      direction: {
        vertical: ['!w-full !flex-row items-center gap-4'],
        horizontal: ['flex-col gap-2']
      },
      size: {
        s: 'w-20',
        m: 'w-32',
        l: 'w-44'
      }
    }
  }),
  headerLeftSection: cva(['items-center gap-2'], {
    variants: {
      direction: {
        vertical: [],
        horizontal: ['w-full grow']
      }
    }
  }),
  headerRightSection: cva(['w-full items-center gap-2'], {
    variants: {
      direction: {
        vertical: ['justify-between'],
        horizontal: ['']
      }
    }
  }),
  titleContainer: cva([''], {
    variants: {
      onClick: {
        true: ['cursor-pointer hover:underline hover:opacity-70']
      },
      direction: {
        vertical: [''],
        horizontal: ['pr-3']
      }
    }
  }),
  title: cva(['text-s'], {
    variants: {
      active: {
        true: ['font-bold text-neutral-900'],
        false: ['text-neutral-800']
      },
      size: {
        s: 'text-xs',
        m: 'text-s',
        l: 'text-m'
      }
    }
  }),
  desc: cva(['text-xxs text-neutral-600']),
  children: cva(['flex-nowrap gap-8 '], {
    variants: {
      direction: {
        vertical: ['pl-4'],
        horizontal: []
      }
    }
  }),
  border: cva(['border-solid border-neutral-300 group-last/item:hidden'], {
    variants: {
      direction: {
        vertical: ['left-0 -my-2 min-h-8 border-l-1'],
        horizontal: [' flex-grow border-t-1']
      }
    }
  })
}

export const TimelineItem: FC<TimelineItemProps> = ({
  title,
  active,
  leftSection,
  rightSection,
  desc,
  onClick,
  children,
  ...other
}) => {
  const ctx = useTimelineContext()
  const direction = ctx?.direction
  const size = ctx?.size || 'm'

  return (
    <Flex className={timelineItemCVA.root()} {...other}>
      <Flex className={timelineItemCVA.header({ direction, size })}>
        <Flex
          row
          className={timelineItemCVA.headerLeftSection({
            direction
          })}
        >
          {Boolean(leftSection) && leftSection}
          {ctx?.direction === 'horizontal' && (
            <div
              className={timelineItemCVA.border({
                direction
              })}
            />
          )}
        </Flex>
        <Flex row className={timelineItemCVA.headerRightSection({ direction })}>
          <Flex
            onClick={onClick}
            className={timelineItemCVA.titleContainer({
              onClick: Boolean(onClick),
              direction
            })}
          >
            {desc && <Text className={timelineItemCVA.desc()}>{desc}</Text>}
            {title && (
              <Text className={timelineItemCVA.title({ active, size })}>
                {title}
              </Text>
            )}
          </Flex>
          <Flex row className='justify-end'>
            {rightSection}
          </Flex>
        </Flex>
      </Flex>
      <Flex row className={timelineItemCVA.children({ direction })}>
        {ctx?.direction === 'vertical' && (
          <div className={timelineItemCVA.border({ direction })} />
        )}
        {Boolean(children) && <div>{children}</div>}
      </Flex>
    </Flex>
  )
}
